import React, { useCallback, useEffect, useState } from 'react';
import { Mail, CheckCircle, Send, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../../components/Common/PageHeader';
import emailAdminService from '../../services/emailAdminService';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperAdminUser } from '../../utils/platformRoles';
import { Navigate } from 'react-router-dom';
import { getAppHomePath } from '../../constants/navigation';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';

const DEFAULT_RAW_TEMPLATE = `{
  "from": "{{from}}",
  "to": "{{to}}",
  "subject": "{{subject}}",
  "text": "{{text}}"
}`;

const RESEND_RAW_TEMPLATE = `{
  "from": "{{from_formatted}}",
  "to": ["{{to}}"],
  "subject": "{{subject}}",
  "html": "{{html}}",
  "text": "{{text}}"
}`;

function inferHttpAuth(cfg = {}) {
  if ((cfg.http_auth || '').toLowerCase() === 'bearer') return 'bearer';
  if ((cfg.http_auth || '').toLowerCase() === 'basic') return 'basic';
  return String(cfg.host || '').toLowerCase().includes('resend.com') ? 'bearer' : 'basic';
}

const DEFAULT_FORM_FIELDS = [
  { name: 'from', value: '{{from}}' },
  { name: 'to', value: '{{to}}' },
  { name: 'subject', value: '{{subject}}' },
  { name: 'text', value: '{{text}}' },
];

function parseFormFields(template) {
  if (!template) return DEFAULT_FORM_FIELDS.map((row) => ({ ...row }));
  if (Array.isArray(template)) {
    return template.map((row) => ({ name: row.name || '', value: row.value || '' }));
  }
  try {
    const parsed = JSON.parse(template);
    if (Array.isArray(parsed)) {
      return parsed.map((row) => ({ name: row.name || '', value: row.value || '' }));
    }
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed).map(([name, value]) => ({ name, value: String(value ?? '') }));
    }
  } catch {
    return DEFAULT_FORM_FIELDS.map((row) => ({ ...row }));
  }
  return DEFAULT_FORM_FIELDS.map((row) => ({ ...row }));
}

function payloadTemplateForSave(form) {
  if (form.auth_mode !== 'api_key') return '';
  if (form.payload_type === 'raw') return form.payload_template || DEFAULT_RAW_TEMPLATE;
  return JSON.stringify((form.form_fields || []).filter((row) => (row.name || '').trim()));
}

const emptyForm = {
  name: '',
  from_email: '',
  from_name: '',
  host: '',
  port: 587,
  username: '',
  password: '',
  api_key: '',
  auth_mode: 'password',
  http_auth: 'basic',
  payload_type: 'form_data',
  payload_template: DEFAULT_RAW_TEMPLATE,
  form_fields: DEFAULT_FORM_FIELDS.map((row) => ({ ...row })),
  use_tls: true,
  is_active: false,
};

export default function EmailProviders() {
  const { t } = useTranslation('admin');

  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const isApiKey = form.auth_mode === 'api_key';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await emailAdminService.listProviders());
    } catch (e) {
      toast.error(userFacingError(e, 'admin:loadProvidersError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!isSuperAdminUser(user)) {
    return <Navigate to={getAppHomePath(user)} replace />;
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        provider_type: form.auth_mode === 'api_key' ? 'http' : 'smtp',
        from_email: form.from_email,
        from_name: form.from_name || null,
        is_active: form.is_active,
        smtp: {
          host: form.host,
          port: Number(form.port),
          username: form.username || null,
          password: form.auth_mode === 'password' ? (form.password || undefined) : undefined,
          api_key: form.auth_mode === 'api_key' ? (form.api_key || undefined) : undefined,
          auth_mode: form.auth_mode,
          http_auth: form.auth_mode === 'api_key' ? form.http_auth : undefined,
          payload_type: form.auth_mode === 'api_key' ? form.payload_type : 'form_data',
          payload_template: payloadTemplateForSave(form),
          use_tls: form.use_tls,
        },
      };
      if (editingId) {
        await emailAdminService.updateProvider(editingId, payload);
        toast.success('Provedor atualizado');
      } else {
        await emailAdminService.createProvider(payload);
        toast.success('Provedor criado');
      }
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(userFacingError(e, 'saveError'));
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id) => {
    try {
      await emailAdminService.activateProvider(id);
      toast.success('Provedor ativado');
      await load();
    } catch (e) {
      toast.error(userFacingError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este provedor?')) return;
    try {
      await emailAdminService.deleteProvider(id);
      toast.success('Provedor excluído');
      await load();
    } catch (e) {
      toast.error(userFacingError(e));
    }
  };

  const testSend = async (id) => {
    if (!testEmail) {
      toast.error(t('testEmailRequired'));
      return;
    }
    try {
      const r = await emailAdminService.testProvider(id, testEmail);
      toast.success(r.success ? t('testEmailSent') : t('testEmailFail'));
    } catch (e) {
      toast.error(userFacingError(e, 'admin:testEmailError'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("emailProvidersTitle")}
        description="Usuário/senha envia por SMTP. API key envia por POST com Basic Auth."
        icon={Mail}
      />

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
        <h3 className="font-semibold text-gray-800">{editingId ? 'Editar provedor' : 'Novo provedor'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {['name', 'from_email', 'from_name'].map((f) => (
            <input
              key={f}
              className="form-input"
              placeholder={f}
              type="text"
              value={form[f] || ''}
              onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))}
            />
          ))}
          <input
            className="form-input md:col-span-2"
            placeholder={isApiKey ? 'URL da API (POST)' : 'host SMTP'}
            type="text"
            value={form.host || ''}
            onChange={(e) => setForm((s) => ({ ...s, host: e.target.value }))}
          />
          {!isApiKey && (
            <input
              className="form-input"
              placeholder="port"
              type="number"
              value={form.port}
              onChange={(e) => setForm((s) => ({ ...s, port: e.target.value }))}
            />
          )}
          <div className="md:col-span-2 flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="auth_mode"
                checked={form.auth_mode === 'password'}
                onChange={() => setForm((s) => ({ ...s, auth_mode: 'password' }))}
              />
              Usuário e senha (SMTP)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="auth_mode"
                checked={form.auth_mode === 'api_key'}
                onChange={() => setForm((s) => ({ ...s, auth_mode: 'api_key' }))}
              />
              API key (POST)
            </label>
          </div>
          {isApiKey && (
            <>
              <div className="md:col-span-2 flex flex-wrap items-center gap-4 text-sm">
                <span className="text-gray-600">Cabeçalho de autenticação:</span>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="http_auth"
                    checked={form.http_auth === 'basic'}
                    onChange={() => setForm((s) => ({ ...s, http_auth: 'basic' }))}
                  />
                  Basic (usuário + API key)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="http_auth"
                    checked={form.http_auth === 'bearer'}
                    onChange={() => setForm((s) => ({ ...s, http_auth: 'bearer' }))}
                  />
                  Bearer (Authorization: Bearer)
                </label>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setForm((s) => ({
                    ...s,
                    auth_mode: 'api_key',
                    http_auth: 'bearer',
                    payload_type: 'raw',
                    host: 'https://api.resend.com/emails',
                    username: '',
                    payload_template: RESEND_RAW_TEMPLATE,
                  }))}
                >
                  Preencher modelo Resend
                </button>
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-4 text-sm">
                <span className="text-gray-600">Tipo de parâmetro:</span>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="payload_type"
                    checked={form.payload_type === 'form_data'}
                    onChange={() => setForm((s) => ({ ...s, payload_type: 'form_data' }))}
                  />
                  form-data
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="payload_type"
                    checked={form.payload_type === 'raw'}
                    onChange={() => setForm((s) => ({ ...s, payload_type: 'raw' }))}
                  />
                  raw (JSON)
                </label>
              </div>
              <p className="md:col-span-2 text-xs text-gray-500">
                Variáveis: <code>{'{{from}}'}</code> <code>{'{{from_formatted}}'}</code> <code>{'{{to}}'}</code> <code>{'{{subject}}'}</code> <code>{'{{text}}'}</code>
                {' '}(opcional: <code>{'{{html}}'}</code>)
              </p>
              {form.payload_type === 'raw' ? (
                <textarea
                  className="form-input md:col-span-2 font-mono text-sm min-h-[160px]"
                  value={form.payload_template}
                  onChange={(e) => setForm((s) => ({ ...s, payload_template: e.target.value }))}
                  placeholder={DEFAULT_RAW_TEMPLATE}
                />
              ) : (
                <div className="md:col-span-2 space-y-2">
                  {(form.form_fields || []).map((row, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        className="form-input flex-1"
                        placeholder="nome do parâmetro"
                        value={row.name}
                        onChange={(e) => setForm((s) => ({
                          ...s,
                          form_fields: s.form_fields.map((item, i) => (i === index ? { ...item, name: e.target.value } : item)),
                        }))}
                      />
                      <input
                        className="form-input flex-[2]"
                        placeholder="valor, ex: {{to}}"
                        value={row.value}
                        onChange={(e) => setForm((s) => ({
                          ...s,
                          form_fields: s.form_fields.map((item, i) => (i === index ? { ...item, value: e.target.value } : item)),
                        }))}
                      />
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => setForm((s) => ({
                          ...s,
                          form_fields: s.form_fields.filter((_, i) => i !== index),
                        }))}
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => setForm((s) => ({
                      ...s,
                      form_fields: [...s.form_fields, { name: '', value: '' }],
                    }))}
                  >
                    Adicionar parâmetro
                  </button>
                </div>
              )}
            </>
          )}
          {form.auth_mode === 'password' ? (
            <>
              <input
                className="form-input"
                placeholder="username"
                value={form.username || ''}
                onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))}
              />
              <input
                className="form-input"
                placeholder="password"
                type="password"
                value={form.password || ''}
                onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
              />
            </>
          ) : (
            <>
              <input
                className="form-input"
                placeholder="username (opcional, padrão: api)"
                value={form.username || ''}
                onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))}
              />
              <input
                className="form-input"
                placeholder="API key"
                type="password"
                value={form.api_key || ''}
                onChange={(e) => setForm((s) => ({ ...s, api_key: e.target.value }))}
              />
            </>
          )}
          {!isApiKey && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.use_tls}
                onChange={(e) => setForm((s) => ({ ...s, use_tls: e.target.checked }))}
              />
              TLS
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
            />
            Ativar ao salvar
          </label>
        </div>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <input
          className="form-input w-full mb-4"
          placeholder="E-mail para teste"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
        />
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
              <div>
                <div className="font-medium">
                  {p.name}{' '}
                  {p.is_active && <span className="text-green-600 text-xs">(ativo)</span>}
                </div>
                <div className="text-sm text-gray-600">
                  {p.from_email} — {p.config?.auth_mode === 'api_key' ? `POST ${p.config?.payload_type || 'form_data'}` : 'SMTP'} — {p.config?.host}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {!p.is_active && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => activate(p.id)}>
                    <CheckCircle className="w-4 h-4" /> Ativar
                  </button>
                )}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => testSend(p.id)}>
                  <Send className="w-4 h-4" /> Teste
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    setEditingId(p.id);
                    setForm({
                      ...emptyForm,
                      name: p.name,
                      from_email: p.from_email,
                      from_name: p.from_name || '',
                      host: p.config?.host || '',
                      port: p.config?.port || 587,
                      username: p.config?.username || '',
                      use_tls: p.config?.use_tls !== false,
                      auth_mode: p.config?.auth_mode === 'api_key' ? 'api_key' : 'password',
                      http_auth: inferHttpAuth(p.config),
                      payload_type: p.config?.payload_type === 'raw' ? 'raw' : 'form_data',
                      payload_template: p.config?.payload_type === 'raw' ? (p.config?.payload_template || DEFAULT_RAW_TEMPLATE) : DEFAULT_RAW_TEMPLATE,
                      form_fields: parseFormFields(p.config?.payload_type === 'raw' ? null : p.config?.payload_template),
                      is_active: !!p.is_active,
                    });
                  }}
                >
                  Editar
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => remove(p.id)}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {!items.length && <p className="text-gray-500 text-sm">Nenhum provedor configurado.</p>}
        </div>
      </div>
    </div>
  );
}
